import Money from "../../ui/Money"
import { useState } from "react"
import { FiChevronDown } from "react-icons/fi"
import { useFilingSidebarPanel } from "../../ui/FilingSidebarAccordion"

type FeeLine = {
  label: string
  amount: number
}

type FeesBoxV2Props = {
  total?: number
  serviceLines?: FeeLine[]
  officialLines?: FeeLine[]
  subtotalServices?: number
  subtotalOfficial?: number
  // The price only means something once there is something to price
  ready?: boolean
  placeholderText?: string
}

function formatFee(value: number) {
  return <Money amount={value} />
}

export default function FeesBoxV2({
  total = 350,
  serviceLines = [],
  officialLines = [{ label: "Official fee for one design", amount: 350 }],
  subtotalServices = serviceLines.reduce((sum, line) => sum + line.amount, 0),
  subtotalOfficial = officialLines.reduce((sum, line) => sum + line.amount, 0),
  ready = true,
  placeholderText = "The price is shown once there is something to price."
}: FeesBoxV2Props) {
  const [feesOpen, toggleFees] = useFilingSidebarPanel("fees")
  const totalFee = formatFee(total);

  if (!ready) {
    return (
      <div className="w-full shrink-0 box-border overflow-hidden rounded-md border border-gray-200 bg-white text-sm shadow-sm">
        <div className="flex w-full items-center justify-between px-4 py-3 bg-dantzBlue text-white">
          <span className="font-medium text-white">Total Fees</span>
          <span className="text-lg font-bold tabular-nums text-white">—</span>
        </div>
        <p className="px-4 py-3 text-xs text-secondary4">{placeholderText}</p>
      </div>
    )
  }

  return (
    <div className="w-full shrink-0 box-border overflow-hidden rounded-md border border-gray-200 bg-white text-sm shadow-sm">
      <button
        type="button"
        onClick={toggleFees}
        className="flex w-full items-center justify-between px-4 py-3 bg-dantzBlue text-white"
        aria-expanded={feesOpen}
      >
        <span className="font-medium text-white">Total Fees</span>
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold tabular-nums text-white">
            {totalFee}
          </span>
          <FiChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${feesOpen ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </div>
      </button>

      {feesOpen && (
        <div className="space-y-3 px-4 py-3 text-xs bg-secondary1 text-white">
          {serviceLines.length > 0 && <FeeCategory title="Service Fees" lines={serviceLines} subtotal={subtotalServices} />}
          {officialLines.length > 0 && <FeeCategory title="Official Fees" lines={officialLines} subtotal={subtotalOfficial} />}
          <div className="flex justify-between border-t border-secondary3 pt-2 font-semibold">
            <span>Total</span>
            <span>{formatFee(total)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function FeeCategory({
  title,
  lines,
  subtotal
}: {
  title: string
  lines: FeeLine[]
  subtotal: number
}) {
  const [open, setOpen] = useState(false)

  return (
    <section className="border-b border-secondary2 pb-3 last:border-0">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 text-left"
        aria-expanded={open}
      >
        <span className="font-semibold">{title}</span>
        <span className="flex items-center gap-2">
          <span className="text-white/80">Subtotal</span>
          <span className="font-semibold tabular-nums">{formatFee(subtotal)}</span>
          <FiChevronDown
            className={`h-4 w-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            aria-hidden="true"
          />
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {lines.map((line) => (
            <div key={line.label} className="flex justify-between gap-3">
              <span className="text-white/80">{line.label}</span>
              <span className="font-semibold tabular-nums text-white">
                {formatFee(line.amount)}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
